import _ from "lodash";

import ACLStore from "lib/aclStore";
import ACLStorage from "lib/aclService";
import { store } from "store/createStore";

import {
  getMemoizedFunction,
  hashString,
  parseConfigurationValue,
} from "Utils";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";
import { getIsAYWisePermsSetupEnabled } from "Utils/ACLStoreUtils";
import { getIsCombineCurriculumHomePageEnabled } from "Platform/modules/PlatformUtils";
import {
  getAcademicYearForCurriculumProgram,
  getSelectedAcademicYearId,
  getSelectedCurriculumProgram,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import { getUserInfo } from "Login/modules/LoginModule";
import { readConfigurationFragmentV2 } from "modules/CommonGraphqlHelpers";

import {
  CURRICULUM_TYPE_DP,
  ELEMENT_TYPE_DP_OBJECTIVES,
  ELEMENT_TYPE_DP_AIM,
  ELEMENT_TYPE_MYP_ATL,
} from "Constants/stringConstants";
import {
  CURRICULUM_INSIGHTS_PERMISSION,
  PLANNING_INSIGHTS_CONFIGURE_UNITS_PERMISSION,
} from "Constants/permissionConstants";
import {
  DEFAULT_SELECTED_ENTITY,
  ELEMENTS_WITH_TASK_COUNT_MAP,
} from "./Constants";
import { AVATAR_COLORS } from "Constants/colorConstants";

export const isCurriculumInsightsEnabled = () => {
  const state = store?.getState();
  let isPermissionEnabled = false;

  const userInfo = getUserInfo();
  const userId = userInfo.id;

  const currentCurriculumProgram = getSelectedCurriculumProgram(state);
  const curriculumProgramId = currentCurriculumProgram?.id;

  const isAYWisePermsSetupEnabled = getIsAYWisePermsSetupEnabled();

  const academicYearId = getAcademicYearForCurriculumProgram(state, {
    curriculumProgramId,
  })?.id;

  if (curriculumProgramId && academicYearId) {
    const configurationEntityGroups = [
      {
        entityType: "CURRICULUM_PROGRAM",
        entityId: curriculumProgramId,
      },
      ...(isAYWisePermsSetupEnabled
        ? [{ entityType: "ACADEMIC_YEAR", entityId: academicYearId }]
        : [{ entityType: "USER", entityId: userId }]),
    ];

    const configurationVariables = {
      configurationEntityGroupFilters: [
        {
          configurationEntityGroups,
        },
      ],
    };

    const settingValue = readConfigurationFragmentV2(
      configurationVariables,
      CURRICULUM_INSIGHTS_PERMISSION
    );

    isPermissionEnabled = parseConfigurationValue(settingValue);
  }

  const isPermissionBasedAccess =
    isPermissionEnabled || ACLStore.can("IdentityAccess:CurriculumInsights");

  return isPermissionBasedAccess;
};

export const getIsNonCourseInsights = () => {
  const state = store?.getState();

  const curriculumProgramId = getSelectedCurriculumId(state);
  const academicYearId = getAcademicYearForCurriculumProgram(state, {
    curriculumProgramId,
  })?.id;

  return !getIsLearningCoursesEnabled({ curriculumProgramId, academicYearId });
};

export const getUpdatedDepth = ({ elementType, depth }) => {
  const isNonCourseInsights = getIsNonCourseInsights();

  switch (elementType) {
    case ELEMENT_TYPE_MYP_ATL:
      return isNonCourseInsights ? depth - 1 : depth;

    case ELEMENT_TYPE_DP_OBJECTIVES:
      return depth - 1;

    case ELEMENT_TYPE_DP_AIM:
      return depth - 2;

    default:
      return depth - 1;
  }
};

const _getCustomPlannerElementSetsQueryVariables = ({
  currentCurriculumProgram,
}) => {
  if (_.isEmpty(currentCurriculumProgram)) {
    return null;
  }

  const {
    id,
    nodeInterfaceType: curriculumNodeInterfaceType,
  } = currentCurriculumProgram;

  const filters = {
    isIncludedInInsights: true,
  };

  return {
    id,
    curriculumNodeInterfaceType,
    category: ["CUSTOM"],
    // discrepancy between filter types from BE
    filters,
    _filters: filters,
  };
};

export const getCustomPlannerElementSetsQueryVariables = getMemoizedFunction(
  _getCustomPlannerElementSetsQueryVariables
);

export const getDPSubjectSubtitle = ({ subject, showLevelTags = true }) => {
  if (!subject) {
    return null;
  }

  const assessmentYearTitle = _.get(
    subject,
    "contentSubject.assessmentYearTitle",
    ""
  );
  const subtitleItems = [assessmentYearTitle];

  if (showLevelTags) {
    const levelLabels = _.map(subject.variants, "level.label");
    if (levelLabels.length > 0) {
      subtitleItems.push(levelLabels.join(", "));
    }
  }

  return _.join(subtitleItems, " • ");
};

export const getGenericQueryVariables = ({ queryType }) => {
  const state = store?.getState();

  const isNonCourseInsights = getIsNonCourseInsights();
  const userInfo = getUserInfo();
  const { org_id: organizationId } = userInfo;

  const academicYearId = getSelectedAcademicYearId(state);
  const currentCurriculumProgram = getSelectedCurriculumProgram(state);

  const {
    id: curriculumProgramId,
    type: curriculumProgramType,
    nodeInterfaceType: curriculumInterfaceType,
  } = currentCurriculumProgram;

  const isDP = curriculumProgramType === CURRICULUM_TYPE_DP;

  const yearGroupGradeId = _.get(
    state,
    "curriculumInsights.yearGroupSelector.selectedYearGroup.grade.id",
    null
  );

  switch (queryType) {
    case "GRADES":
      return {
        id: curriculumProgramId,
        curriculumInterfaceType,
      };

    case "SUBJECTS": {
      const filters = {
        archivalState: "ALL",
      };

      if (isDP) {
        filters.types = ["GENERIC"];
      }

      return {
        id: curriculumProgramId,
        curriculumInterfaceType,
        filters,
      };
    }

    case "DEPARTMENTS": {
      const subjectGroupFilters = {};

      if (isDP) {
        subjectGroupFilters.types = ["GENERIC"];
      }

      return {
        id: curriculumProgramId,
        curriculumInterfaceType,
        subjectGroupFilters,
      };
    }

    case "CLASSES": {
      const variables = {
        curriculumProgramId,
        curriculumInterfaceType,
        filters: {
          academicYearIds: academicYearId,
          archivalState: "ALL",
        },
      };

      if (!isNonCourseInsights) {
        variables.filters.isCourseIncludedInInsights = true;
      }

      if (isDP) {
        variables.filters.subjectTypes = ["GENERIC"];

        if (yearGroupGradeId) {
          variables.filters = {
            ...variables.filters,
            grades: {
              includeIds: [yearGroupGradeId],
            },
          };
        }
      }

      return variables;
    }

    case "LEARNING_COURSES": {
      const variables = {
        curriculumProgramId,
        curriculumInterfaceType,
        filters: {
          academicYears: academicYearId,
          isPlanningInsightQuery: true,
          archivalState: "ALL",
        },
      };

      if (isDP) {
        variables.filters.subjectTypes = ["GENERIC"];

        if (yearGroupGradeId) {
          variables.filters = {
            ...variables.filters,
            grades: [yearGroupGradeId],
          };
        }
      }

      return variables;
    }

    case "STUDENTS": {
      const variables = {
        organizationId,
        filters: {
          curriculumProgramIds: curriculumProgramId,
          academicYearId,
          archivalState: "ALL",
        },
      };

      if (!isNonCourseInsights) {
        variables.filters.isCourseIncludedInInsights = true;
      }
      return variables;
    }

    case "CUSTOM_SETS": {
      return getCustomPlannerElementSetsQueryVariables({
        currentCurriculumProgram,
      });
    }
  }
};

export const getDefaultSelectedCountEntity = () => {
  const isNonCourseInsights = getIsNonCourseInsights();

  // unit count is to be default for non course since course count does not exist for it
  return isNonCourseInsights ? "UNIT" : DEFAULT_SELECTED_ENTITY;
};

export const getCanConfigureUnitsAndCourses = () => {
  if (getIsCombineCurriculumHomePageEnabled()) {
    const state = store?.getState();
    const curriculumProgramId = getSelectedCurriculumId(state);

    return ACLStorage.getPermissionValueByContext(
      PLANNING_INSIGHTS_CONFIGURE_UNITS_PERMISSION,
      [{ entityType: "CURRICULUM_PROGRAM", entityId: curriculumProgramId }]
    );
  }

  return ACLStorage.getPermissionValue(
    PLANNING_INSIGHTS_CONFIGURE_UNITS_PERMISSION
  );
};

export const getIsDownloadsEnabled = (params = {}) => {
  const { currentInsight = {} } = params;

  if (!_.isEmpty(currentInsight)) {
    return _.get(currentInsight, "isDownloadEnabled", false);
  }

  return true;
};

export const isTaskCountVisible = ({ curriculumProgramType, elementType }) => {
  const allowedElements = _.get(
    ELEMENTS_WITH_TASK_COUNT_MAP,
    curriculumProgramType,
    []
  );
  return _.includes(allowedElements, elementType);
};

export const getIsNewViewsEnabled = () => {
  return ACLStore.can(
    "FeatureFlag:DepartmentStudentClassViewForCurriculumInsights"
  );
};

export const getAvatarData = ({ id, title }) => {
  const color = AVATAR_COLORS[hashString(id) % AVATAR_COLORS.length];
  return { color, avatarName: title };
};
